import React, { useRef, FC, ReactNode, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useVirtual } from 'react-virtual';

const LIST_MAX_HEIGHT = 450;

export type VirtualizedListProps = {
  renderItem?: (index: number) => ReactNode;
  renderLoader?: () => ReactNode;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  size: number;
};

const VirtualizedList: FC<VirtualizedListProps> = ({
  renderItem,
  renderLoader,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  size,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const pageSizeWithLoader =
    hasNextPage || isFetchingNextPage ? size + 1 : size;
  const pageSize = hasNextPage || isFetchingNextPage ? size - 1 : size;
  const rowVirtualizer = useVirtual({
    size: pageSizeWithLoader,
    parentRef,
  });

  useEffect(() => {
    const lastItem =
      rowVirtualizer.virtualItems[rowVirtualizer.virtualItems.length - 1];
    const isLastItemVisible = lastItem?.index > pageSize;

    if (!lastItem) {
      return;
    }

    if (isLastItemVisible && hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    rowVirtualizer.virtualItems,
    pageSize,
  ]);

  return (
    <div
      ref={parentRef}
      css={{
        minHeight: 120,
        maxHeight: LIST_MAX_HEIGHT,
        overflow: 'auto',
      }}
    >
      <div
        css={{
          height: rowVirtualizer.totalSize,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.virtualItems.map((virtualRow) => (
          <div
            key={virtualRow.index}
            ref={virtualRow.measureRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            <div>
              {virtualRow.index > pageSize
                ? isFetchingNextPage
                  ? renderLoader?.()
                  : null // Nothing more to load
                : renderItem?.(virtualRow.index)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

VirtualizedList.displayName = 'VirtualizedList';

VirtualizedList.propTypes = {
  /** Function that renders the item */
  renderItem: PropTypes.func,
  /** Function that renders the item loader */
  renderLoader: PropTypes.func,
  /** List of items */
  size: PropTypes.number.isRequired,
};

export default VirtualizedList;
